import React from 'react';

// @@MENU_CAREERS_v89@@ HQ › Website › "Menu" and "Careers page" tabs.
// Both edit the same site DRAFT as the other tabs (Save draft → Preview → Publish).
// Server-side sanitize() is the real gate (links must be #section, /path,
// https://… or mailto:); these screens just explain the rules.

const S = {
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 14 },
  label: { display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, color: 'var(--hq-muted)', textTransform: 'uppercase', letterSpacing: '.06em' },
  input: { width: '100%', boxSizing: 'border-box' },
  area: { width: '100%', boxSizing: 'border-box', height: 'auto', padding: '10px 12px', resize: 'vertical', lineHeight: 1.5 },
  body: { padding: 20, display: 'flex', flexDirection: 'column', gap: 16 },
  hint: { margin: 0, color: 'var(--hq-muted)', fontSize: 13, lineHeight: 1.5 },
  small: { height: 32, padding: '0 10px', fontSize: 12, width: 'auto', marginTop: 0 },
  row: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', padding: '10px 0', borderBottom: '1px solid var(--hq-line)' },
  warn: { color: '#e8c15a', fontSize: 12 },
};

function Panel({ title, children }) {
  return (
    <div className="hq-panel" style={{ marginBottom: 16 }}>
      <div className="hq-panel-head"><h2>{title}</h2></div>
      <div style={S.body}>{children}</div>
    </div>
  );
}
function Field({ label, value, onChange, max, multiline, rows = 3, placeholder }) {
  return (
    <label style={S.label}>{label}
      {multiline
        ? <textarea className="hq-input" style={S.area} rows={rows} maxLength={max} value={value || ''} onChange={(e) => onChange(e.target.value)} />
        : <input className="hq-input" style={S.input} maxLength={max} placeholder={placeholder} value={value || ''} onChange={(e) => onChange(e.target.value)} />}
    </label>
  );
}

// Mirrors server/lib/site-content.js href() so editors see a problem before saving.
export function linkProblem(h) {
  const s = String(h || '').trim();
  if (!s) return 'A link is required.';
  if (/^#[A-Za-z0-9_-]{1,40}$/.test(s)) return '';
  if (/^\/(?!\/)[A-Za-z0-9\-._~/?#=&%+]*$/.test(s)) return '';
  if (/^https?:\/\/[^\s<>"'\\]+$/i.test(s)) return '';
  if (/^mailto:[^\s<>"'\\]+$/i.test(s)) return '';
  return 'Use #section, /page, https://… or mailto: — this link would be dropped on save.';
}
const SECTION_OF = { '#services': 'services', '#production': 'production', '#stores': 'stores', '#portal': 'portal', '#contact': 'contact' };
const QUICK = [
  ['#services', 'Services section'], ['#production', 'Production section'], ['#stores', 'Stores section'],
  ['#portal', 'Client portal section'], ['#contact', 'Contact form'], ['/jobs', 'Careers page'],
];

export function MenuEditor({ menu, sections, onChange }) {
  const items = menu.items;
  const setItems = (next) => onChange({ ...menu, items: next });
  const setItem = (i, k, v) => setItems(items.map((it, j) => (j === i ? { ...it, [k]: v } : it)));
  const move = (i, d) => { const j = i + d; if (j < 0 || j >= items.length) return; const n = items.slice(); [n[i], n[j]] = [n[j], n[i]]; setItems(n); };
  const setBtn = (key, k, v) => onChange({ ...menu, [key]: { ...menu[key], [k]: v } });
  const hiddenSection = (h) => SECTION_OF[h] && !sections[SECTION_OF[h]];

  return (
    <>
      <Panel title="Menu links">
        <p style={S.hint}>
          Shown across the top of every page (home, Careers and each job) and repeated in the footer.
          Links can jump to a home-page section (<code>#services</code>), open a page on the site (<code>/jobs</code>),
          or go elsewhere (<code>https://…</code>, <code>mailto:</code>).
        </p>
        <div>
          {items.map((it, i) => {
            const prob = linkProblem(it.href);
            return (
              <div key={i} style={S.row}>
                <input className="hq-input" style={{ flex: '1 1 150px' }} aria-label="Menu label" maxLength={30} value={it.label}
                  onChange={(e) => setItem(i, 'label', e.target.value)} />
                <input className="hq-input" style={{ flex: '2 1 220px', borderColor: prob ? '#ff8c86' : undefined }} aria-label="Menu link" maxLength={300}
                  list="hq-menu-quick" placeholder="#section, /page or https://…" value={it.href} onChange={(e) => setItem(i, 'href', e.target.value)} />
                <select className="hq-select" style={{ flex: '1 1 170px' }} aria-label="When to show" value={it.show} onChange={(e) => setItem(i, 'show', e.target.value)}>
                  <option value="always">Always shown</option>
                  <option value="hiring">Only while hiring</option>
                  <option value="hidden">Hidden</option>
                </select>
                <button type="button" className="hq-signout" style={S.small} aria-label="Move up" onClick={() => move(i, -1)}>↑</button>
                <button type="button" className="hq-signout" style={S.small} aria-label="Move down" onClick={() => move(i, 1)}>↓</button>
                <button type="button" className="hq-signout" style={{ ...S.small, color: '#ff7a73' }} onClick={() => setItems(items.filter((_, j) => j !== i))}>Remove</button>
                {(prob || hiddenSection(it.href)) && (
                  <div style={{ flexBasis: '100%', ...S.warn }}>
                    {prob || 'That section is switched off in Sections, so this link is hidden too.'}
                  </div>
                )}
              </div>
            );
          })}
          <datalist id="hq-menu-quick">{QUICK.map(([h, l]) => <option key={h} value={h}>{l}</option>)}</datalist>
          {items.length < 8
            ? <button type="button" className="hq-signout" style={{ width: 'auto', padding: '0 14px', marginTop: 12 }}
                onClick={() => setItems([...items, { label: '', href: '', show: 'always' }])}>+ Add link</button>
            : <p style={{ ...S.hint, marginTop: 12 }}>8 links is the maximum — the menu gets crowded past that.</p>}
        </div>
        <p style={S.hint}>“Only while hiring” shows the link only when at least one job is open in Hiring. On phones the top links collapse and only the buttons show; the footer always lists them.</p>
      </Panel>

      <Panel title="Header buttons">
        <div style={S.grid}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14 }}>
              <input type="checkbox" checked={menu.login.show} onChange={(e) => setBtn('login', 'show', e.target.checked)} /> Show the outline button
            </label>
            <Field label="Label" value={menu.login.label} max={30} onChange={(v) => setBtn('login', 'label', v)} />
            <Field label="Link (blank = HQ client login)" value={menu.login.href} max={300} placeholder="Blank = HQ login page" onChange={(v) => setBtn('login', 'href', v)} />
            {menu.login.href && linkProblem(menu.login.href) && <span style={S.warn}>{linkProblem(menu.login.href)}</span>}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14 }}>
              <input type="checkbox" checked={menu.cta.show} onChange={(e) => setBtn('cta', 'show', e.target.checked)} /> Show the green button
            </label>
            <Field label="Label" value={menu.cta.label} max={30} onChange={(v) => setBtn('cta', 'label', v)} />
            <Field label="Link" value={menu.cta.href} max={300} placeholder="#contact" onChange={(v) => setBtn('cta', 'href', v)} />
            {linkProblem(menu.cta.href) && menu.cta.show && <span style={S.warn}>{linkProblem(menu.cta.href)}</span>}
            {hiddenSection(menu.cta.href) && <span style={S.warn}>That section is switched off, so this button is hidden.</span>}
          </div>
        </div>
      </Panel>
    </>
  );
}

export function CareersEditor({ careers, assets, onChange }) {
  const set = (k, v) => onChange({ ...careers, [k]: v });
  return (
    <>
      <Panel title="Careers page (/jobs)">
        <div style={S.grid}>
          <Field label="Eyebrow" value={careers.eyebrow} max={80} onChange={(v) => set('eyebrow', v)} />
          <label style={S.label}>Header photo
            <select className="hq-select" style={S.input} value={careers.image || ''} onChange={(e) => set('image', e.target.value || null)}>
              <option value="default-pano">Warehouse panorama (built-in)</option>
              <option value="default-overhead">Warehouse overhead (built-in)</option>
              <option value="">No photo (plain dark)</option>
              {assets.map((a) => <option key={a.id} value={a.id}>{a.filename || a.id.slice(0, 8)}</option>)}
            </select>
          </label>
          <Field label="Headline (white)" value={careers.headline_1} max={60} onChange={(v) => set('headline_1', v)} />
          <Field label="Headline (green)" value={careers.headline_2} max={60} onChange={(v) => set('headline_2', v)} />
        </div>
        <Field label="Intro" multiline value={careers.intro} max={400} onChange={(v) => set('intro', v)} />
        <div style={S.grid}>
          <Field label="Label above the list" value={careers.list_label} max={40} onChange={(v) => set('list_label', v)} />
          <Field label="When nothing is open" value={careers.empty} max={200} onChange={(v) => set('empty', v)} />
          <Field label="…followed by a link to the contact form" value={careers.empty_link} max={40} onChange={(v) => set('empty_link', v)} />
          <Field label="“We’re hiring” label (home-page strip + job pages)" value={careers.strip} max={40} onChange={(v) => set('strip', v)} />
        </div>
      </Panel>

      <Panel title="Every job page">
        <p style={S.hint}>These headings appear on every posting. Each job’s own text, duties, requirements and questions are edited in Hiring › Postings.</p>
        <div style={S.grid}>
          <Field label="Role — small label" value={careers.role_kicker} max={40} onChange={(v) => set('role_kicker', v)} />
          <Field label="Role — heading" value={careers.role_heading} max={80} onChange={(v) => set('role_heading', v)} />
          <Field label="Growth box title" value={careers.growth_label} max={40} onChange={(v) => set('growth_label', v)} />
          <Field label="Requirements — small label" value={careers.reqs_kicker} max={40} onChange={(v) => set('reqs_kicker', v)} />
          <Field label="Requirements — heading" value={careers.reqs_heading} max={80} onChange={(v) => set('reqs_heading', v)} />
          <Field label="Apply — small label" value={careers.apply_kicker} max={40} onChange={(v) => set('apply_kicker', v)} />
          <Field label="Apply — heading" value={careers.apply_heading} max={80} onChange={(v) => set('apply_heading', v)} />
          <Field label="Submit button" value={careers.apply_button} max={40} onChange={(v) => set('apply_button', v)} />
        </div>
        <Field label="Apply — intro" multiline rows={2} value={careers.apply_intro} max={300} onChange={(v) => set('apply_intro', v)} />
        <div style={S.grid}>
          <Field label="After applying — heading" value={careers.thanks_heading} max={80} onChange={(v) => set('thanks_heading', v)} />
          <Field label="After applying — message" multiline rows={2} value={careers.thanks_body} max={300} onChange={(v) => set('thanks_body', v)} />
        </div>
      </Panel>
    </>
  );
}
